import React, { useEffect } from "react";
import moment from "moment";
import { get, map, first } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Menu,
  Checkbox,
  Typography,
} from "@mui/material";
import { DATE_FILTER_OPTIONS } from "../../v2_actions/date";
import { briefGalleryList } from "../../v2_actions/brief_gallery";
import { useGalleryFilterSearchParams } from "./useGalleryFilterSearchParams";
import { useEventPageContext } from "./EventPageContext";
import { styles } from "./styles";
import { makeLogger } from "../../utils/logger";

// remove this for event filtering
const DATE_FILTER_OPTIONS_FILTERED = DATE_FILTER_OPTIONS.filter((item) => item.value !== 'placeholder_date_filter_option') 

const logger = makeLogger({
  enabled: true,
  label: "FilterDate",
  color: "pink",
});

function mapDateRange(dateRange) {
  switch (dateRange) {
    case "last_24_hrs":
      return { from: moment().format(), to: moment().format() };
    case "last_2_days":
      return {
        from: moment().subtract(2, "days").format(),
        to: moment().format(),
      };
    case "last_7_days":
      return {
        from: moment().subtract(7, "days").format(),
        to: moment().format(),
      };
    case "last_30_days":
      return {
        from: moment().subtract(31, "days").format(),
        to: moment().format(),
      };
    case "last_90_days":
      return {
        from: moment().subtract(90, "days").format(),
        to: moment().format(),
      };
    case "last_180_days":
      return {
        from: moment().subtract(180, "days").format(),
        to: moment().format(),
      };
    case "last_365_days":
      return {
        from: moment().subtract(365, "days").format(),
        to: moment().format(),
      };
    case "last_2_years":
      return {
        from: moment().subtract(2, "years").format(),
        to: moment().format(),
      };
    case "last_3_years":
      return {
        from: moment().subtract(3, "years").format(),
        to: moment().format(),
      };
    case "custom":
      return { from: null, to: null };
    case null:
      return { from: null, to: null };
    default:
      if (dateRange !== get(first(DATE_FILTER_OPTIONS_FILTERED), "value")) {
        console.error(`Unknown date filter option: ${dateRange}`);
      }
      return { from: null, to: null };
  }
}

export default function FilterDate() {
  useSelector(() => ({})); // force redux state reconcile
  const dispatch = useDispatch();
  const eventPageCtx = useEventPageContext();
  const [galleryFilterSearchParams, syncGalleryFilterSearchParam] =
    useGalleryFilterSearchParams();
  const [muiMenuAnchor, setMuiMenuAnchor] = React.useState(null);
  const isMenuOpen = Boolean(muiMenuAnchor);
  const calendar_filter = get(briefGalleryList.getFilter(), "calendar_filter");

  const calendarFilterFromSearchParam = galleryFilterSearchParams.date;

  const handleClickListItem = (event) => {
    setMuiMenuAnchor(event.currentTarget);
  };

  const handleMenuItemClick = (option, index) => {
    syncGalleryFilterSearchParam("date", option?.value);
    setMuiMenuAnchor(null);
  };
  const handleCheckboxClicked = (ev) => {
    if (!ev.target.checked) {
      setMuiMenuAnchor(null);
      syncGalleryFilterSearchParam("date", "");
    }
  };

  const handleClose = () => {
    setMuiMenuAnchor(null);
  };

  useEffect(() => {
    const { from, to } = mapDateRange(
      calendarFilterFromSearchParam ?? "placeholder_date_filter_option",
    );

    logger.log("FX: calendarFilterFromSearchParam", {
      calendarFilterFromSearchParam,
      from,
      to,
    });

    eventPageCtx.updateListFilter({
      shot_at_date_from: from,
      shot_at_date_to: to,
      _callSource: "FilterDate",
    });
  }, [calendarFilterFromSearchParam]);

  logger.log({
    DATE_FILTER_OPTIONS_FILTERED,
    dateFilterValue: calendarFilterFromSearchParam,
    calendar_filter,
  });

  const filterLabel = DATE_FILTER_OPTIONS_FILTERED.filter(
    (opt) => opt.value === calendarFilterFromSearchParam,
  )
    .map((opt) => opt.label)
    .concat("Recent")
    .shift();
  const isFilterActive = !!calendarFilterFromSearchParam;

  return (
    <div>
      <Typography
        button
        sx={styles.filterListTitle}
        onClick={handleClickListItem}>
        Date
      </Typography>
      <List component="nav" sx={styles.filterList}>
        <ListItem
          button
          sx={styles.filterListItem}
          aria-expanded={isMenuOpen ? "true" : undefined}>
          <Checkbox
            checked={isFilterActive}
            size="small"
            onChange={handleCheckboxClicked}
          />
          <ListItemText
            onClick={handleClickListItem}
            primary=""
            sx={styles.filterListItemText}
            secondary={<ListItemText primary={filterLabel} />}
          />
        </ListItem>
      </List>
      <Menu
        anchorEl={muiMenuAnchor}
        open={isMenuOpen}
        onClose={handleClose}
        MenuListProps={{
          role: "listbox",
        }}>
        <MenuItem
          value={null}
          disabled={calendarFilterFromSearchParam === null}
          selected={calendarFilterFromSearchParam === null}
          onClick={(event) => handleMenuItemClick(null, 0)}>
          Recent
        </MenuItem>
        {map(DATE_FILTER_OPTIONS_FILTERED, (option, index) => (
          <MenuItem
            key={get(option, "value")}
            disabled={calendarFilterFromSearchParam === get(option, "value")}
            selected={calendarFilterFromSearchParam === get(option, "value")}
            onClick={(event) => handleMenuItemClick(option, index)}>
            {get(option, "label")}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
